import { useState, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axiosInstance';
import { updateUploadProgress } from '../../../components/common/UploadProgress';
import {
  SYSTEM_ALLOWED_MIME_TYPES,
  EXTENSION_TO_MIME,
} from '../../../constants/formCrafter.constant';

/**
 * useFileUpload Hook
 *
 * Handles file uploads with progress tracking and validation.
 * Uses system-wide allowed MIME types from formCrafter.constant.js
 * which must match the backend uploadMiddleware.js configuration.
 *
 * @param {boolean} isAdminUpload - Whether to use admin upload endpoint
 * @param {string} fieldName - Unique identifier for the upload field
 */
export const useFileUpload = (isAdminUpload = false, fieldName = 'default') => {
  const [state, setState] = useState({
    uploading: false,
    progress: 0,
    uploadedFiles: [],
    error: null,
  });

  const endpoint = isAdminUpload
    ? '/api/v1/files/admin/upload'
    : '/api/v1/files/upload';

  useEffect(() => {
    updateUploadProgress(fieldName, {
      uploading: state.uploading,
      progress: state.progress,
    });
  }, [state.uploading, state.progress, fieldName]);

  const validateFile = useCallback((file, maxSizeMB = 100) => {
    if (!file) {
      return { isValid: false, error: 'No file provided', warnings: [] };
    }

    const warnings = [];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds ${maxSizeMB}MB limit`,
        warnings,
      };
    }

    // Use system-wide allowed extensions (derived from EXTENSION_TO_MIME keys)
    const supportedExtensions = Object.keys(EXTENSION_TO_MIME).map((ext) =>
      ext.replace('.', '')
    );

    // Use system-wide allowed MIME types + generic types for browser compatibility
    const genericMimeTypes = [
      'application/octet-stream',
      'application/x-download',
      'binary/octet-stream',
    ];
    const supportedMimeTypes = [
      ...SYSTEM_ALLOWED_MIME_TYPES,
      ...genericMimeTypes,
    ];

    // Get file extension
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    const mimeType = file.type.toLowerCase();

    const isMimeSupported = supportedMimeTypes.some((type) =>
      mimeType.includes(type)
    );
    const isExtensionSupported = supportedExtensions.includes(fileExtension);

    // Generic MIME types (browser compatibility)
    const isGenericMime = genericMimeTypes.includes(mimeType);

    if (isGenericMime && isExtensionSupported) {
      warnings.push(
        `Generic MIME type detected (${mimeType}), but extension is valid (.${fileExtension})`
      );
    } else if (!isMimeSupported && !isExtensionSupported) {
      return {
        isValid: false,
        error: `Unsupported file type: .${fileExtension} (${mimeType}). Supported types: ${supportedExtensions.join(', ')}`,
        warnings,
      };
    } else if (!isMimeSupported && isExtensionSupported) {
      warnings.push(
        `MIME type not recognized (${mimeType}), but extension (.${fileExtension}) is supported`
      );
    }

    return { isValid: true, error: null, warnings };
  }, []);

  const uploadSingleFile = useCallback(
    async (file, onProgress = null, skipValidation = false) => {
      if (!file) {
        toast.error('Please select a file to upload', {
          position: 'top-center',
        });
        return null;
      }

      // Validate file unless explicitly skipped
      if (!skipValidation) {
        const validation = validateFile(file);
        if (!validation.isValid) {
          toast.error(validation.error, {
            position: 'top-center',
          });
          return null;
        }

        // Show warnings if any
        validation.warnings.forEach((warning) => {
          console.warn('File upload warning:', warning);
        });
      }

      const formData = new FormData();
      formData.append('file', file);

      // Set uploading state immediately
      flushSync(() => {
        setState((prev) => ({
          ...prev,
          uploading: true,
          progress: 0,
          error: null,
        }));
      });

      try {
        const response = await axiosInstance.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 minutes for large files
          onUploadProgress: (event) => {
            const progress = Math.round((event.loaded * 100) / event.total);

            // Update progress immediately
            flushSync(() => {
              setState((prev) => ({ ...prev, progress }));
            });

            // Notify custom callback if provided
            if (onProgress) {
              onProgress(progress);
            }
          },
        });

        if (response.data.success) {
          const serverFile = response.data.data;

          // Normalize file structure for consistent usage
          const normalizedFile = {
            ...serverFile,
            id: serverFile.file_id || serverFile.id,
            name: serverFile.original_name || serverFile.name,
            size: Number(serverFile.file_size || serverFile.size || 0),
            type: serverFile.mime_type || serverFile.type,
          };

          // Show 100% completion briefly
          flushSync(() => {
            setState((prev) => ({ ...prev, progress: 100 }));
          });

          // Brief pause to show completion
          await new Promise((resolve) => setTimeout(resolve, 800));

          // Reset to idle state
          flushSync(() => {
            setState((prev) => ({
              ...prev,
              uploading: false,
              progress: 0,
              uploadedFiles: [...prev.uploadedFiles, normalizedFile],
              error: null,
            }));
          });

          toast.success('File uploaded successfully!', {
            position: 'top-center',
          });

          return normalizedFile;
        } else {
          throw new Error(response.data.message || 'Upload failed');
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'File upload failed';

        // Reset to error state
        flushSync(() => {
          setState((prev) => ({
            ...prev,
            uploading: false,
            progress: 0,
            error: errorMessage,
          }));
        });

        // Note: Error toast is handled by axios interceptor for consistency
        return null;
      }
    },
    [endpoint, validateFile]
  );

  const uploadMultipleFiles = useCallback(
    async (files, onProgress = null) => {
      if (!files || files.length === 0) {
        toast.error('Please select at least one file to upload', {
          position: 'top-center',
        });
        return [];
      }

      const uploadPromises = files.map((file, index) =>
        uploadSingleFile(file, (progress) => {
          if (onProgress) {
            // Calculate combined progress across all files
            const overallProgress = (index * 100 + progress) / files.length;
            onProgress(Math.round(overallProgress));
          }
        })
      );

      try {
        const results = await Promise.allSettled(uploadPromises);
        const successfulUploads = results
          .filter((result) => result.status === 'fulfilled' && result.value)
          .map((result) => result.value);

        const failedCount = results.filter(
          (result) => result.status === 'rejected'
        ).length;

        if (failedCount > 0) {
          toast.warning(
            `${successfulUploads.length} of ${files.length} files uploaded successfully`,
            { position: 'top-center' }
          );
        }

        return successfulUploads;
      } catch {
        return [];
      }
    },
    [uploadSingleFile]
  );

  const resetState = useCallback(() => {
    setState({
      uploading: false,
      progress: 0,
      uploadedFiles: [],
      error: null,
    });
  }, []);

  const removeUploadedFile = useCallback((fileId) => {
    setState((prev) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((file) => file.id !== fileId),
    }));
  }, []);

  return {
    // Upload state
    uploading: state.uploading,
    uploadProgress: state.progress, // Keep original name for compatibility
    uploadedFiles: state.uploadedFiles,
    error: state.error,

    // Upload functions
    uploadFile: uploadSingleFile, // Keep original name for compatibility
    uploadMultipleFiles,
    resetUploadState: resetState, // Keep original name for compatibility
    removeUploadedFile,

    // File validation
    validateFile,
  };
};

export default useFileUpload;
